import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import NewsCard from '@/components/feature/NewsCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import { allArticles, popularKeywords, queryArticles } from '@/utils/news';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  useSeo({
    title: keyword ? `搜索“${keyword}” - 9·1牛网` : '搜索资讯 - 9·1牛网',
    description: keyword
      ? `在 9·1牛网搜索“${keyword}”的相关资讯内容，快速定位你需要的新闻、娱乐与生活信息。`
      : '在 9·1牛网搜索新闻、娱乐、生活等领域的资讯内容。',
    keywords: keyword ? `${keyword},9·1牛网,资讯搜索` : '9·1牛网,资讯搜索,关键词检索',
  });

  const [input, setInput] = useState(keyword);
  const [showSuggest, setShowSuggest] = useState(false);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => queryArticles({ keyword, sort: 'latest' }), [keyword]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const page = Math.min(Math.max(1, pageParam), totalPages);
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const suggestions = useMemo(() => {
    const kw = input.trim().toLowerCase();
    if (!kw) return [];
    return allArticles
      .filter((a) => a.title.toLowerCase().includes(kw))
      .slice(0, 6);
  }, [input]);

  const submit = (value: string) => {
    setShowSuggest(false);
    const v = value.trim();
    if (v) setSearchParams({ q: v });
    else setSearchParams({});
  };

  const goPage = (p: number) => {
    setSearchParams({ q: keyword, page: String(p) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: '搜索' }]} />

        <div className="mt-4 max-w-3xl">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">搜索资讯</h1>
          <p className="text-sm text-foreground-500 mt-2">输入关键词，快速定位你需要的资讯内容</p>

          <form
            className="mt-5 relative"
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              type="text"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setShowSuggest(true);
              }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              placeholder="搜索新闻、娱乐、生活等关键词"
              className="w-full h-12 md:h-14 pl-12 pr-28 text-sm md:text-base rounded-full border border-background-300 bg-background-100 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 md:h-11 px-4 md:px-6 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>

            {showSuggest && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 z-30 rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onMouseDown={() => submit(s.title)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer truncate"
                  >
                    <i className="ri-search-line text-foreground-400 mr-2" />
                    {s.title}
                  </button>
                ))}
              </div>
            )}
          </form>
        </div>

        {keyword ? (
          <section className="mt-8">
            <p className="text-sm text-foreground-600 mb-5">
              关键词 “<span className="text-primary-600 font-medium">{keyword}</span>” 共找到{' '}
              <span className="text-primary-600 font-medium">{results.length}</span> 条结果
            </p>
            {pageItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed border-background-300 bg-background-100 text-center">
                <i className="ri-emotion-sad-line text-4xl text-foreground-400 mb-3" />
                <p className="text-base font-medium text-foreground-800">没有找到与该关键词相关的内容</p>
                <p className="text-sm text-foreground-500 mt-1">换个关键词试试，或浏览下面的热门推荐</p>
                <Link
                  to="/"
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
                <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-lg">
                  {popularKeywords.map((k) => (
                    <Link
                      key={k}
                      to={`/search?q=${encodeURIComponent(k)}`}
                      className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {k}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {pageItems.map((article) => (
                    <NewsCard key={article.id} article={article} variant="row" />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={goPage} />
              </>
            )}
          </section>
        ) : (
          <section className="mt-10">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 inline-flex items-center gap-2">
              <i className="ri-fire-line text-accent-500" /> 热门搜索
            </h2>
            <div className="flex flex-wrap gap-2">
              {popularKeywords.map((k) => (
                <Link
                  key={k}
                  to={`/search?q=${encodeURIComponent(k)}`}
                  className="px-4 py-2 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line mr-1" />
                  {k}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}